import React from 'react';

/* ============================================================================
   FlexiOss Bespoke Icon Set
   Modern, minimal medical line-art. Two-tone: the primary stroke inherits
   `currentColor` (deep navy in light surfaces, white/royal-blue on dark),
   while a single warm-orange (#E87A20) accent gives each glyph a distinctive,
   on-brand focal detail. Stroke width 1.6 for an elegant, scientific feel.
   ========================================================================== */

const ORANGE = '#E87A20';

const svgBase = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = ({ children, className, ...props }) => (
  <svg {...svgBase} className={className} {...props}>
    {children}
  </svg>
);

/** Surgical scissors — "Surgical Moldability" */
export const SurgicalScissorsIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <circle cx="6" cy="6.5" r="2.3" />
    <circle cx="6" cy="17.5" r="2.3" />
    <line x1="8" y1="7.8" x2="20" y2="16.5" />
    <line x1="8" y1="16.2" x2="20" y2="7.5" />
    <circle cx="12.5" cy="12" r="0.9" fill={accent} stroke={accent} />
    <line x1="14" y1="12" x2="21" y2="12" stroke={accent} strokeDasharray="2 2.2" />
  </Icon>
);

/** Bone with milling gear — "Can Be Milled for Personalization" */
export const BoneMillIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <path d="M4 5.2a2.2 2.2 0 0 1 3.7 1.4v1c0 .5-.2.9-.5 1.3L5.7 11v2l1.5 2.1c.3.4.5.8.5 1.3v1a2.2 2.2 0 0 1-3.7 1.4 2.2 2.2 0 0 1-.6-2v-.9c0-.5.2-.9.5-1.3L5.4 13v-2L3.9 8.9c-.3-.4-.5-.8-.5-1.3v-1a2.2 2.2 0 0 1 .6-1.4z" />
    <circle cx="16.5" cy="12" r="3.4" stroke={accent} />
    <circle cx="16.5" cy="12" r="0.9" fill={accent} stroke={accent} />
    <line x1="16.5" y1="7.4" x2="16.5" y2="8.6" stroke={accent} />
    <line x1="16.5" y1="15.4" x2="16.5" y2="16.6" stroke={accent} />
    <line x1="11.9" y1="12" x2="13.1" y2="12" stroke={accent} />
    <line x1="19.9" y1="12" x2="21.1" y2="12" stroke={accent} />
  </Icon>
);

/** Bone with upward growth vector — "Accelerated Ossification" */
export const OssificationIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <path d="M4 7a2 2 0 0 1 3.4 1.3v1c0 .4-.1.8-.4 1.1L5.6 13v0l1.4 1.6c.3.3.4.7.4 1.1v1A2 2 0 0 1 4 19a2 2 0 0 1-.5-1.3v-1c0-.4.1-.8.4-1.1L5.3 14v0l-1.4-1.6c-.3-.3-.4-.7-.4-1.1v-1A2 2 0 0 1 4 7z" transform="rotate(-45 5.5 13)" />
    <line x1="16" y1="20" x2="16" y2="6" stroke={accent} />
    <polyline points="12 10 16 6 20 10" stroke={accent} />
    <line x1="11.5" y1="20" x2="20.5" y2="20" strokeDasharray="2 2.4" />
  </Icon>
);

/** Shield protecting against a crossed-out pathogen — "Minimizes Contamination Risk" */
export const BioSafetyIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <path d="M12 2.5l7.5 3.2v5c0 4.9-3.2 9.1-7.5 10.3C7.7 19.8 4.5 15.6 4.5 10.7v-5L12 2.5z" />
    <circle cx="12" cy="11" r="2.6" />
    <line x1="12" y1="6.6" x2="12" y2="8.4" />
    <line x1="12" y1="13.6" x2="12" y2="15.4" />
    <line x1="7.6" y1="11" x2="9.4" y2="11" />
    <line x1="14.6" y1="11" x2="16.4" y2="11" />
    <line x1="8.7" y1="15.3" x2="15.3" y2="6.7" stroke={accent} strokeWidth="1.9" />
  </Icon>
);

/** Droplets soaking into a porous matrix — "High Soaking Capacity / Carrier Matrix" */
export const DrugDeliveryIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <rect x="4" y="11" width="16" height="9" rx="1.6" />
    <line x1="8" y1="14" x2="8" y2="17" />
    <line x1="12" y1="14" x2="12" y2="17" />
    <line x1="16" y1="14" x2="16" y2="17" />
    <path d="M12 3.2c1.7 2.1 2.7 3.7 2.7 5.1A2.7 2.7 0 0 1 12 11a2.7 2.7 0 0 1-2.7-2.7c0-1.4 1-3 2.7-5.1z" fill={accent} stroke={accent} />
    <circle cx="6.5" cy="8.4" r="0.8" fill={accent} stroke={accent} />
    <circle cx="17.5" cy="8.4" r="0.8" fill={accent} stroke={accent} />
  </Icon>
);

/** Sterile package, ambient transport — "Easy to Store & Transport" */
export const SterileStorageIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <path d="M12 3l7.5 4v8l-7.5 4-7.5-4V7l7.5-4z" />
    <polyline points="4.7 7.2 12 11 19.3 7.2" />
    <line x1="12" y1="11" x2="12" y2="19" />
    <path d="M9.2 5.6h5.6" stroke={accent} strokeWidth="1.9" />
    <path d="M10.4 14.4h3.2" stroke={accent} />
  </Icon>
);

/** Two clinicians linked by a referral vector — "Refer a Colleague" */
export const SurgeonTeamIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <circle cx="8" cy="6" r="2.4" />
    <path d="M3.5 19v-1.6A4 4 0 0 1 7.5 13.4h1A4 4 0 0 1 12.5 17.4V19" />
    <circle cx="17.5" cy="8" r="2" />
    <path d="M14.6 19v-1.3a2.9 2.9 0 0 1 2.9-2.9 2.9 2.9 0 0 1 2.9 2.9V19" />
    <path d="M12.8 8.4c1.4-.9 2.7-.9 3.8-.2" stroke={accent} strokeDasharray="2 2" />
    <polyline points="15.9 6.6 16.8 8.1 15.2 8.7" stroke={accent} />
  </Icon>
);

/** Clinician at a microscope — "Hands-on Training in Poland" */
export const MedicalTrainingIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <circle cx="8.5" cy="5" r="2.3" />
    <path d="M4.5 20v-3.2A4 4 0 0 1 8.5 12.8h0A4 4 0 0 1 12.5 16.8V20" />
    <path d="M16.5 17.5a4 4 0 0 0 4-4" stroke={accent} />
    <line x1="18.5" y1="9" x2="18.5" y2="13.5" />
    <circle cx="18.5" cy="6.6" r="1.7" stroke={accent} />
    <line x1="16.6" y1="17.5" x2="20.4" y2="17.5" />
    <line x1="15.4" y1="20" x2="21.6" y2="20" />
  </Icon>
);

/** Research flask with DNA strand — "Research Collaboration" */
export const ResearchLabIcon = ({ accent = ORANGE, ...props }) => (
  <Icon {...props}>
    <path d="M9 3v5.2L4.7 16a2 2 0 0 0 1.8 3h7a2 2 0 0 0 1.8-3L11 8.2V3" />
    <line x1="7.4" y1="3" x2="12.6" y2="3" />
    <path d="M6.4 13.2h7.2" />
    <path d="M9 16.4h2" stroke={accent} />
    <circle cx="10" cy="11.4" r="0.8" fill={accent} stroke={accent} />
    <path d="M18 4c1.6 1.8 1.6 4.2 0 6s-1.6 4.2 0 6" stroke={accent} />
    <line x1="17" y1="6.4" x2="19" y2="6.4" stroke={accent} />
    <line x1="17" y1="13.6" x2="19" y2="13.6" stroke={accent} />
  </Icon>
);
